import { RoleUtils } from "@/services/roles/roles";
import { useAuthStore } from "@/stores/AuthStore";
import { useCursoStore } from "@/stores/CursoStore";
import type { MenuItem } from "primevue/menuitem";
import { useToast } from "primevue/usetoast";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

interface RoleMenuItemOptions {
  activeClass?: string;
  inactiveLabelPrefix?: string;
  onBeforeSwitch?: () => void;
  showActiveRole?: boolean;
}

function normalizeIcon(icon?: string): string {
  if (!icon) {
    return "pi pi-user";
  }

  return icon.startsWith("pi ") ? icon : "pi " + icon;
}

function isCourseBoundRole(role: string): boolean {
  return ["aluno", "coordenador", "secretaria", "coordenador_tcc", "orientador"].includes(role);
}

export function useRoleSwitching() {
  const authStore = useAuthStore();
  const cursoStore = useCursoStore();
  const router = useRouter();
  const toast = useToast();

  const isChangingRole = ref(false);
  const oldRoleIcon = ref("");
  const newRoleIcon = ref("");
  const roleChangeMessage = ref("");

  const currentRole = computed(() => authStore.activeRole || authStore.getDefaultRole());

  async function switchRole(newRole: string): Promise<void> {
    const previousRole = currentRole.value;
    if (previousRole === newRole) {
      return;
    }

    const currentConfig = RoleUtils.getConfig(previousRole);
    const newConfig = RoleUtils.getConfig(newRole);

    isChangingRole.value = true;
    oldRoleIcon.value = normalizeIcon(currentConfig?.icon);
    newRoleIcon.value = normalizeIcon(newConfig?.icon);
    roleChangeMessage.value = "De " + (currentConfig?.label || previousRole) + " para " + (newConfig?.label || newRole);

    try {
      await new Promise(resolve => setTimeout(resolve, 800));

      authStore.switchRole(newRole);
      roleChangeMessage.value = "Carregando ambiente de " + (newConfig?.label || newRole) + "...";

      if (isCourseBoundRole(newRole)) {
        await cursoStore.reloadCursos();
      } else {
        cursoStore.clearCurso();
      }

      await new Promise(resolve => setTimeout(resolve, 400));
      const permissions = authStore.user?.role_details
        ?.find((role) => role.name === newRole)?.permissions ?? [];
      const fallbackRoute = permissions.includes('trabalho.view')
        ? '/projetos'
        : (permissions.includes('evento.view') ? '/eventos' : '/conta');
      await router.push(newConfig?.route || fallbackRoute);
      await new Promise(resolve => setTimeout(resolve, 300));
    } catch (error) {
      console.error("Erro ao trocar perfil:", error);
      toast.add({
        severity: "error",
        summary: "Erro",
        detail: "Não foi possível alternar o perfil. Tente novamente.",
        life: 3000,
      });
    } finally {
      isChangingRole.value = false;
    }
  }

  function buildRoleMenuItems(options: RoleMenuItemOptions = {}): MenuItem[] {
    const activeClass = options.activeClass || "active-role-item";

    const roles = options.showActiveRole === false
      ? authStore.availableRoles.filter(role => currentRole.value !== role)
      : authStore.availableRoles;

    return roles.map(role => {
      const config = RoleUtils.getConfig(role);
      const isActive = currentRole.value === role;
      const label = config?.label || role;

      return {
        label: isActive ? label + " (atual)" : (options.inactiveLabelPrefix || "") + label,
        icon: normalizeIcon(config?.icon),
        class: isActive ? activeClass : "",
        disabled: isActive,
        command: async () => {
          if (isActive) {
            return;
          }

          options.onBeforeSwitch?.();
          await switchRole(role);
        },
      };
    });
  }

  return {
    buildRoleMenuItems,
    currentRole,
    isChangingRole,
    newRoleIcon,
    oldRoleIcon,
    roleChangeMessage,
    switchRole,
  };
}
